// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Routes and query defaults (design doc §10.1).
 *
 * Two defaults are deliberate:
 *
 * - **queries do not retry.** The default three attempts would turn a 400 into three
 *   identical rejections and delay the message the operator needs. The server's errors are
 *   answers, not transient faults; the one genuinely transient case (a dropped connection)
 *   is handled by the user pressing the button again.
 * - **`refetchOnWindowFocus` is off.** A scanning station sits on one tab for a whole
 *   shift, and a refetch on every focus change would reorder a list the operator is
 *   reading from.
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import i18n from "i18next";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from "react-router-dom";
import { Layout } from "@/components/Layout";
import { RequireRole } from "@/components/RequireRole";
import { usePermissions } from "@/hooks/usePermissions";
import { ArchiveCompare } from "@/pages/ArchiveCompare";
import { ArchiveCreate } from "@/pages/ArchiveCreate";
import { ArchiveCurrent } from "@/pages/ArchiveCurrent";
import { ArchiveDetailPage } from "@/pages/ArchiveDetailPage";
import { ArchiveImport } from "@/pages/ArchiveImport";
import { ArchiveInstallations } from "@/pages/ArchiveInstallations";
import { ArchiveMaintenance } from "@/pages/ArchiveMaintenance";
import { ArchiveSearch } from "@/pages/ArchiveSearch";
import { AuditLogs } from "@/pages/AuditLogs";
import { BomDetailPage } from "@/pages/BomDetailPage";
import { BomList } from "@/pages/BomList";
import { Login } from "@/pages/Login";
import { MaintenanceEventPage } from "@/pages/MaintenanceEventPage";
import { ProductTypes } from "@/pages/ProductTypes";
import { SystemPage } from "@/pages/SystemPage";
import { Users } from "@/pages/Users";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false, staleTime: 10_000 },
    mutations: { retry: false },
  },
});

// BOM remarks come back in the request's language (Accept-Language), so what is cached was
// read in the old one. Switching language refetches it rather than showing the previous
// language's remarks until each query happens to go stale.
i18n.on("languageChanged", () => void queryClient.invalidateQueries());

/** Where the app opens: filing for whoever may file, the archives for a visitor (§12.4). */
function StartPage() {
  const { canRecord } = usePermissions();
  return <Navigate to={canRecord ? "/workbench/archive-create" : "/archives"} replace />;
}

// A data router, for `useBlocker`: a form with unsubmitted work holds in-app navigation
// until the operator says to leave (hooks/useLeaveGuard.ts).
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Outside the shell: reaching it means there is no session to show a shell for. */}
      <Route path="/login" element={<Login />} />
      <Route element={<Layout />}>
        <Route index element={<StartPage />} />
        <Route
          path="/workbench/archive-create"
          element={
            <RequireRole allowed={(p) => p.canRecord}>
              <ArchiveCreate />
            </RequireRole>
          }
        />
        <Route
          path="/workbench/archive-import"
          element={
            <RequireRole allowed={(p) => p.canRecord}>
              <ArchiveImport />
            </RequireRole>
          }
        />
        <Route path="/archives" element={<ArchiveSearch />} />
        <Route path="/archives/:archiveId" element={<ArchiveDetailPage />}>
          <Route index element={<Navigate to="current" replace />} />
          <Route path="current" element={<ArchiveCurrent />} />
          {/* The version history moved into the timeline beside the record. The route
            stays as a redirect so links people already have keep working. */}
          <Route path="versions" element={<Navigate to="../current" replace />} />
          <Route path="compare" element={<ArchiveCompare />} />
          <Route path="maintenance" element={<ArchiveMaintenance />} />
          {/* One record on its own, for a link pasted elsewhere (links.ts). */}
          <Route path="maintenance/:eventId" element={<MaintenanceEventPage />} />
          <Route path="installations" element={<ArchiveInstallations />} />
        </Route>
        <Route path="/product-types" element={<ProductTypes />} />
        <Route path="/boms" element={<BomList />} />
        <Route path="/boms/:bomId" element={<BomDetailPage />} />
        <Route
          path="/audit"
          element={
            <RequireRole allowed={(p) => p.isAdmin}>
              <AuditLogs />
            </RequireRole>
          }
        />
        <Route
          path="/users"
          element={
            <RequireRole allowed={(p) => p.isAdmin}>
              <Users />
            </RequireRole>
          }
        />
        <Route path="/system" element={<SystemPage />} />
      </Route>
    </>,
  ),
);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
